import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { 
  Table, 
  Pagination, 
  Button, 
  Input, 
  Card, 
  Row, 
  Col, 
  Modal, 
  Form, 
  InputNumber,
  Typography,
  Space,
  Tag,
  message
} from 'antd';
import {
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined
} from '@ant-design/icons';

const { Title } = Typography;

const LogsPage = () => {
    const [logs, setLogs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [limit] = useState(15);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [deleteForm] = Form.useForm();

    // 获取日志
    const fetchLogs = useCallback(async (page = currentPage, search = searchTerm) => {
        setLoading(true);
        try {
            const response = await API.get('/api/admin/logs', {
                params: { page, limit, search }
            });
            const sortedLogs = response.data.logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setLogs(sortedLogs);
            setCurrentPage(response.data.currentPage);
            setTotalLogs(response.data.totalLogs || response.data.logs.length);
        } catch (error) {
            console.error('获取日志时出错:', error);
            message.error('获取日志失败');
        }
        setLoading(false);
    }, [currentPage, searchTerm, limit]);

    // 初始化加载数据
    useEffect(() => {
        fetchLogs(1, '');
        setInitialized(true);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 当页码变化时重新获取数据
    useEffect(() => {
        if (initialized) {
            fetchLogs(currentPage, searchTerm);
        }
    }, [currentPage, fetchLogs, searchTerm, initialized]);

    // 处理搜索
    const handleSearch = () => {
        setSearchTerm(inputValue);
        setCurrentPage(1);
        fetchLogs(1, inputValue);
    };

    // 处理回车键搜索
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // 处理分页
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // 刷新数据
    const handleRefresh = () => {
        fetchLogs(currentPage, searchTerm);
    };

    // 清空搜索
    const handleClearSearch = () => {
        setInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        fetchLogs(1, '');
    };

    // 删除旧日志
    const handleDeleteOldLogs = async (values) => {
        try {
            const response = await API.delete('/api/admin/logs/delete', { 
                data: { days: values.days } 
            });
            message.success(response.data.message || '删除成功');
            fetchLogs(currentPage, searchTerm);
            setShowDeleteModal(false);
            deleteForm.resetFields();
        } catch (error) {
            console.error('删除日志时出错:', error);
            message.error('删除日志失败');
        }
    };



    // 将 UTC 时间转换为北京时间
    const convertToBeijingTime = (timestamp) => {
        const date = new Date(timestamp);
        const beijingOffset = 8 * 60;
        const utcOffset = date.getTimezoneOffset();
        const beijingTime = new Date(date.getTime() + (beijingOffset + utcOffset) * 60000);
        return beijingTime.toLocaleString('zh-CN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    };

    // 获取HTTP方法的颜色
    const getMethodColor = (method) => {
        const colors = {
            'GET': 'green',
            'POST': 'blue',
            'PUT': 'orange',
            'DELETE': 'red',
            'PATCH': 'purple'
        };
        return colors[method] || 'default';
    };



        // 表格列定义
    const columns = [
        {
            title: '用户名',
            dataIndex: 'username',
            key: 'username',
            width: 180,
            render: (username) => (
                <Tag icon={<UserOutlined />} color="blue">
                    {username}
                </Tag>
            ),
        },
        {
            title: 'IP地址',
            dataIndex: 'ip',
            key: 'ip',
            width: 140,
        },
        {
            title: 'IP属地',
            dataIndex: 'ipLocation',
            key: 'ipLocation',
            width: 280,
            render: (location) => location || '未知',
        },
        {
            title: '访问路由',
            dataIndex: 'route',
            key: 'route',
            ellipsis: true,
        },
        {
            title: '请求方法',
            dataIndex: 'method',
            key: 'method',
            width: 100,
            render: (method) => (
                <Tag color={getMethodColor(method)}>
                    {method}
                </Tag>
            ),
        },
        {
            title: '操作时间',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (timestamp) => convertToBeijingTime(timestamp),
        },
    ];

        return (
        <div style={{ padding: '24px' }}>
            {/* 页面标题 */}
            <div style={{ marginBottom: '24px' }}>
                <Title level={2}>操作日志管理</Title>
            </div>

            {/* 操作栏 */}
            <Card style={{ marginBottom: '16px' }}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Input.Search
                            placeholder="搜索用户名、IP地址、路由、请求方法..."
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onSearch={handleSearch}
                            onPressEnter={handleKeyPress}
                            style={{ maxWidth: 400 }}
                            enterButton="搜索"
                        />
                        {searchTerm && (
                            <Button 
                                type="link" 
                                onClick={handleClearSearch}
                                style={{ marginLeft: 8 }}
                            >
                                清空搜索
                            </Button>
                        )}
                    </Col>
                    <Col>
                        <Space>
                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={handleRefresh}
                            >
                                刷新
                            </Button>
                            <Button 
                                icon={<DeleteOutlined />} 
                                danger
                                onClick={() => setShowDeleteModal(true)}
                            >
                                删除日志
                            </Button>
                        </Space>
                    </Col>
                </Row>
                {searchTerm && (
                    <div style={{ marginTop: 8, color: '#666' }}>
                        当前搜索: {searchTerm} | 共 {totalLogs} 条记录
                    </div>
                )}
            </Card>

            {/* 日志表格 */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={logs}
                    rowKey={(record, index) => `${record.timestamp}-${index}`}
                    loading={loading}
                    pagination={false}
                    scroll={{ x: 800 }}
                    size="small"
                />
                
                {/* 分页 */}
                {totalLogs > 0 && (
                    <div style={{ 
                        marginTop: '16px', 
                        textAlign: 'center',
                        borderTop: '1px solid #f0f0f0',
                        paddingTop: '16px'
                    }}>
                        <Pagination
                            current={currentPage}
                            total={totalLogs}
                            pageSize={limit}
                            onChange={handlePageChange}
                            showTotal={(total, range) => 
                                `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`
                            }
                            showSizeChanger={false}
                        />
                    </div>
                )}
            </Card>

            {/* 删除日志模态框 */}
            <Modal
                title="删除日志"
                open={showDeleteModal}
                onCancel={() => {
                    setShowDeleteModal(false);
                    deleteForm.resetFields();
                }}
                footer={null}
                width={400}
            >
                <Form
                    form={deleteForm}
                    layout="vertical"
                    onFinish={handleDeleteOldLogs}
                    initialValues={{ days: 7 }}
                >
                    <Form.Item
                        name="days"
                        label="删除几天前的日志"
                        rules={[
                            { required: true, message: '请输入要删除的天数' },
                            { type: 'number', min: 1, message: '天数必须大于0' }
                        ]}
                    >
                        <InputNumber
                            placeholder="请输入天数"
                            style={{ width: '100%' }}
                            min={1}
                            max={365}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => {
                                setShowDeleteModal(false);
                                deleteForm.resetFields();
                            }}>
                                取消
                            </Button>
                            <Button type="primary" danger htmlType="submit">
                                确认删除
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default LogsPage;
